import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import { useTheme } from '@mui/material/styles';
import { ExternalLink } from 'lucide-react';
import CarrierResources from 'tailwind-components/CarrierResources/CarrierResources';
import FAQs from 'tailwind-components/FAQs/FAQs';
import ScrollableTabs from 'tailwind-components/ScrollableTabs/ScrollableTabs';
import Separator from 'tailwind-components/Separator/Separator';
import useNotifyTourPageReady from 'tailwind-components/PortalWalkthrough/useNotifyTourPageReady';
import useTabUrlState from 'hooks/useTabUrlState';
import NewsAndIndustryIntel from './NewsAndIndustryIntel';
import { getCompany } from 'helpers/getCompanyVariables';

const isOneill = getCompany() === 'oneill';

const WEBINARS_URL =
  'https://events.zoom.us/eo/AmfzhkpIzjDR9u0lk7fpJT4T3uFrSEEj8Mh_HFMzUUynQFN_XFJt~AggLXsr32QYFjq8BlYLZ5I06Dg';

const TABS = [
  { id: 'carrier-resources', label: 'Carrier Resources' },
  ...(isOneill ? [{ id: 'news', label: 'News & Industry Intel' }] : []),
  { id: 'faqs', label: 'FAQs' },
  ...(isOneill
    ? [
        {
          id: 'webinars',
          label: 'Webinars & Events',
          external: true,
          dataTour: 'resources-tab-webinars'
        }
      ]
    : [])
];

const noop = () => {};

// selectable tab ids for URL sync (webinars is an external link, not a tab state)
const TAB_IDS = TABS.filter(tab => !tab.external).map(tab => tab.id);

function ResourceTabLabel({ label, external, selected, theme }) {
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        typography: 'body2',
        fontWeight: selected ? 600 : 500,
        whiteSpace: 'nowrap',
        color: selected
          ? theme.palette.primary.main
          : theme.palette.text.secondary
      }}
    >
      {label}
      {external && (
        <ExternalLink
          size={14}
          style={{ flexShrink: 0, opacity: 0.7 }}
          aria-hidden='true'
        />
      )}
    </Box>
  );
}

ResourceTabLabel.propTypes = {
  label: PropTypes.string.isRequired,
  external: PropTypes.bool,
  selected: PropTypes.bool.isRequired,
  theme: PropTypes.object.isRequired
};

export default function Resources({
  setLocalLoader,
  localLoader,
  faqs,
  carriers,
  appointedCarriers,
  carrierFaqs,
  carrierLoader,
  getFAQsData,
  getAppointmentsViewData,
  getCarrierFaqs
}) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useTabUrlState({
    param: 'tab',
    tabIds: TAB_IDS,
    defaultTabId: 'carrier-resources',
    correctInvalidToDefault: true
  });
  useEffect(() => {
    setLocalLoader(false);
  }, []);

  useNotifyTourPageReady(localLoader);

  const handleTabChange = (_event, nextId) => {
    const tab = TABS.find(t => t.id === nextId);
    if (tab?.external) {
      window.open(WEBINARS_URL, '_blank', 'noopener,noreferrer');
      return;
    }
    setActiveTab(nextId);
  };

  const headerSlot = (
    <div className='shrink-0 mb-4'>
      <p className='m-0 pb-4 text-center text-3xl font-bold text-gray-900'>
        Resources
      </p>
      <ScrollableTabs
        data-tour='resources-tabs'
        value={activeTab}
        onChange={handleTabChange}
        aria-label='Resources tabs'
      >
        {TABS.map(tab => (
          <Tab
            key={tab.id}
            value={tab.id}
            {...(tab.dataTour ? { 'data-tour': tab.dataTour } : {})}
            label={
              <ResourceTabLabel
                label={tab.label}
                external={tab.external}
                selected={activeTab === tab.id}
                theme={theme}
              />
            }
          />
        ))}
      </ScrollableTabs>
      <Separator />
    </div>
  );

  return (
    <div className='flex h-full w-full min-h-0 flex-col'>
      {/* single header/tabs instance — avoids the flicker from 3 duplicated MUI Tabs */}
      {headerSlot}
      <div
        className={
          activeTab !== 'carrier-resources' ? 'hidden' : 'min-h-0 flex-1'
        }
      >
        <CarrierResources
          tabActive={activeTab === 'carrier-resources'}
          carriers={carriers}
          appointedCarriers={appointedCarriers}
          carrierFaqs={carrierFaqs}
          carrierLoader={carrierLoader}
          getAppointmentsViewData={getAppointmentsViewData}
          getCarrierFaqs={getCarrierFaqs}
          setLocalLoader={noop}
        />
      </div>
      <div className={activeTab !== 'news' ? 'hidden' : 'min-h-0 flex-1'}>
        <NewsAndIndustryIntel />
      </div>
      <div className={activeTab !== 'faqs' ? 'hidden' : 'min-h-0 flex-1'}>
        <FAQs
          faqs={faqs}
          getFAQsData={getFAQsData}
          setLocalLoader={noop}
          disableUrlNav
        />
      </div>
    </div>
  );
}

Resources.propTypes = {
  setLocalLoader: PropTypes.func.isRequired,
  localLoader: PropTypes.bool,
  faqs: PropTypes.array,
  carriers: PropTypes.object,
  appointedCarriers: PropTypes.object,
  carrierFaqs: PropTypes.array,
  carrierLoader: PropTypes.bool,
  getFAQsData: PropTypes.func.isRequired,
  getAppointmentsViewData: PropTypes.func.isRequired,
  getCarrierFaqs: PropTypes.func.isRequired
};
